import React from "react";
import type { ApolloClient } from "@apollo/client";
import { Col, Form, Row } from "react-bootstrap";
import { trim } from "lodash";
import { Formik } from "formik";
import "react-datepicker/dist/react-datepicker.css";
import { connect, type ConnectedProps } from "react-redux";
import { userService } from "services/userService";
import {
  getUserState,
  updateIraAccountEmploymentPosition,
  updateIraAccountTrustedContact,
  updateIraAccountUserCitizenship,
} from "actions/userActions";

import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import Button from "components/Button";
import Alert from "components/Alert";
import InfoTooltip from "components/InfoTooltip";
import "./IndividualEnroll.scss";
import { countryValues } from "statics/countriesAndStates";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { userConstants } from "actions/types";
import {
  booleanValue,
  EMAIL_MESSAGE,
  NAME_MESSAGE,
  noWhitespaceRegex,
  PRINTABLE_ASCII_MESSAGE,
  testEmail,
  testName,
  testPrintableAsciiCharacters,
  whiteSpaceError,
  ALPHANUMERIC_SPACES_MESSAGE,
  testAlphanumericSpaces,
} from "utils/fieldValidators";
import { scrollToTop } from "utils/dom";
import { ScrollToFieldError } from "utils/form";
import type { RootState } from "types/state";

/* eslint-disable react/no-children-prop */

import * as yup from "yup";
const positionRegex = /^[\w\s-,]+$/;

const schema = yup.object({
  isCitizen: yup
    .string()
    .required("Please indicate your citizenship status")
    .oneOf(["true", "false"], "Please select Yes or No")
    .label("Citizen of the United States"),
  birthCountry: yup
    .string()
    .test(
      "birth-country",
      PRINTABLE_ASCII_MESSAGE,
      testPrintableAsciiCharacters
    )
    .when("isCitizen", {
      is: "false",
      then: (s) => s.required("Please select the country you were born in"),
      otherwise: (s) => s,
    })
    .label("Country you were born in"),
  citizenshipCountry: yup
    .string()
    .test(
      "citizenship-country",
      PRINTABLE_ASCII_MESSAGE,
      testPrintableAsciiCharacters
    )
    .when("isCitizen", {
      is: "false",
      then: (s) => s.required("Please select your citizenship country"),
      otherwise: (s) => s,
    })
    .label("Country of citizenship"),
  position: yup
    .string()
    .trim()
    .label("Your Job or Position Title")
    .test("position", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
    .test("position", ALPHANUMERIC_SPACES_MESSAGE, testAlphanumericSpaces)
    .matches(
      positionRegex,
      "Job title can only contain alphanumeric characters and the following characters: '-', and ','"
    )
    .required(),
  trustedContact: yup.boolean().required().label("Trusted Contact"),
  trustedFirstName: yup
    .string()
    .matches(noWhitespaceRegex, whiteSpaceError)
    .test(
      "trusted-first-name",
      ALPHANUMERIC_SPACES_MESSAGE,
      testAlphanumericSpaces
    )
    .when("trustedContact", {
      is: true,
      then: (s) =>
        s.required().test("is-valid-first-name", NAME_MESSAGE, testName),
      otherwise: (s) => s.test("is-valid-first-name", NAME_MESSAGE, testName),
    })
    .label("Contact First Name"),
  trustedLastName: yup
    .string()
    .matches(noWhitespaceRegex, whiteSpaceError)
    .test(
      "trusted-last-name",
      ALPHANUMERIC_SPACES_MESSAGE,
      testAlphanumericSpaces
    )
    .when("trustedContact", {
      is: true,
      then: (s) =>
        s.required().test("is-valid-last-name", NAME_MESSAGE, testName),
      otherwise: (s) => s.test("is-valid-last-name", NAME_MESSAGE, testName),
    })
    .label("Contact Last Name"),
  trustedEmail: yup
    .string()
    .matches(noWhitespaceRegex, whiteSpaceError)
    .when("trustedContact", {
      is: true,
      then: (s) => s.test("email", EMAIL_MESSAGE, testEmail).required(),
      otherwise: (s) => s,
    })
    .label("Contact Email"),
});

const loadingSelector = createLoadingSelector([
  userConstants.USER_ON_BOARDING_EMPLOYMENT_POSITION_UPDATE,
  userConstants.USER_ON_BOARDING_CITIZENSHIP_UPDATE,
  userConstants.USER_ON_BOARDING_TRUSTED_CONTACT_UPDATE,
]);

const errorSelector = createErrorSelector([
  userConstants.USER_ON_BOARDING_EMPLOYMENT_POSITION_UPDATE,
  userConstants.USER_ON_BOARDING_CITIZENSHIP_UPDATE,
  userConstants.USER_ON_BOARDING_TRUSTED_CONTACT_UPDATE,
]);

const mapStateToProps = (state: RootState) => ({
  userState: (state.user.userState as { state: string }).state,
  error: errorSelector(state),
  isFetching: loadingSelector(state),
});

const mapDispatchToProps = {
  updateIraAccountEmploymentPosition,
  updateIraAccountUserCitizenship,
  updateIraAccountTrustedContact,
  getUserState,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface EmploymentPositionState {
  position: string;
}

interface UserCitizenshipState {
  citizenshipCountry: string;
  permanentResident: boolean;
  birthCountry: string;
}

interface TrustedContactState {
  trustedContact: boolean;
  trustedFirstName: string;
  trustedLastName: string;
  trustedEmail: string;
}

interface State {
  employmentPosition: EmploymentPositionState;
  userCitizenship: UserCitizenshipState;
  trustedContact: TrustedContactState;
  isCitizen: string;
  submitted: boolean;
  loading: boolean;
}

// Formik form values: spread of the three state sub-objects plus isCitizen.
type ExtraDetailsFormValues = TrustedContactState &
  EmploymentPositionState &
  UserCitizenshipState & {
    isCitizen: string;
  };

class IndividualExtraDetails extends React.Component<Props, State> {
  _isMounted = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      employmentPosition: {
        position: "",
      },
      userCitizenship: {
        citizenshipCountry: "",
        permanentResident: false,
        birthCountry: "",
      },
      trustedContact: {
        trustedContact: false,
        trustedFirstName: "",
        trustedLastName: "",
        trustedEmail: "",
      },

      isCitizen: "",
      submitted: false,
      loading: false,
    };

    userService
      .getIraAccountForm(this.props.client as ApolloClient<object>)
      .then(
        (data) => {
          // Boundary cast: the GetIraAccountForm query types these fields as
          // nullable, but the form contract (and initial state) treats them as
          // string/boolean. Values are assigned verbatim, matching the original
          // untyped behavior.
          let employmentPosition: EmploymentPositionState = {
            position: data.employmentStatus!.position as string,
          };

          let userCitizenship: UserCitizenshipState = {
            citizenshipCountry: data.identity!.citizenshipCountry as string,
            permanentResident: false,
            birthCountry: data.identity!.birthCountry as string,
          };

          let trustedContact: TrustedContactState = {
            trustedContact: data.trustedContact!.trustedContact as boolean,
            trustedFirstName: data.trustedContact!.firstName as string,
            trustedLastName: data.trustedContact!.lastName as string,
            trustedEmail: data.trustedContact!.email as string,
          };

          if (this._isMounted) {
            this.setState({
              submitted: false,
              trustedContact,
              employmentPosition,
              userCitizenship,
              loading: false,
            });
          }
        },
        () => {
          if (this._isMounted) {
            this.setState({
              submitted: false,
              loading: false,
            });
          }
        }
      );
  }

  componentDidMount() {
    this._isMounted = true;
    scrollToTop();
    window.analytics.page("Individual Information");
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  // PRESERVED BUG (conversion-only): the `= false` default never fires (Formik
  // always supplies values); the cast keeps the runtime `false` default verbatim
  // while typing the parameter for the body's field accesses.
  submitIndividual = async (
    values: ExtraDetailsFormValues = false as unknown as ExtraDetailsFormValues
  ) => {
    let employmentPosition = {
      position: trim(values.position),
    };

    let userCitizenship = {
      citizenshipCountry: values.citizenshipCountry,
      permanentResident: false,
      birthCountry: values.birthCountry,
    };

    if (values.isCitizen === "true") {
      userCitizenship.citizenshipCountry = "USA";
      userCitizenship.birthCountry = "";
      userCitizenship.permanentResident = true;
    } else {
      userCitizenship.permanentResident = false;
    }

    let trustedContact = {
      trustedContact: booleanValue(values.trustedContact),
      firstName: values.trustedFirstName,
      lastName: values.trustedLastName,
      email: values.trustedEmail,
    };

    const promises = [
      this.props.updateIraAccountEmploymentPosition(
        this.props.client as ApolloClient<object>,
        employmentPosition
      ),
      this.props.updateIraAccountUserCitizenship(
        this.props.client as ApolloClient<object>,
        userCitizenship
      ),
      this.props.updateIraAccountTrustedContact(
        this.props.client as ApolloClient<object>,
        trustedContact
      ),
    ];

    await Promise.all(promises).then(() => {
      void this.props.getUserState(this.props.client as ApolloClient<object>);
    });

    window.analytics.track("Submitted Additional Individual Information", {
      userState: this.props.userState,
    });

    if (this._isMounted) {
      this.setState({
        submitted: true,
      });
    }
  };

  render() {
    if (this.state.loading) {
      return <div></div>;
    }
    return this.renderExtraDetailsForm();
  }

  renderExtraDetailsForm = () => {
    const { trustedContact, employmentPosition, userCitizenship, isCitizen } =
      this.state;

    var articleStyle = {
      paddingBottom: 35,
    };

    return (
      <div className="individual-extra-details">
        <Formik
          validateOnChange={false}
          validationSchema={schema}
          onSubmit={async (values) => {
            await this.submitIndividual(values);
          }}
          enableReinitialize={true}
          initialValues={{
            ...trustedContact,
            ...employmentPosition,
            ...userCitizenship,
            isCitizen,
          }}
        >
          {({
            handleSubmit,
            handleChange,
            handleBlur,
            setFieldValue,
            values,
            touched,
            errors,
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <ScrollToFieldError />
              <div className="mega-container">
                <div
                  className="step-container is-active"
                  data-circle-percent="30"
                >
                  <section className="page-title-wrap">
                    <article className="text-cell">
                      <p className="page-eyebrow">PERSONAL INFO</p>
                      <p className="page-title">Just a few more details.</p>
                    </article>
                  </section>
                  <div id="form-individual-info">
                    <section className="form-sec-2col">
                      <article className="col-form" style={articleStyle}>
                        <Row className="mb-3">
                          <Form.Group
                            as={Col}
                            sm={5}
                            controlId="formBasicEmployerPosition"
                          >
                            <Form.Label>
                              What is your most recent job title?
                            </Form.Label>

                            <Form.Control
                              name="position"
                              placeholder=""
                              value={values.position}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              isInvalid={touched.position && !!errors.position}
                              isValid={touched.position && !errors.position}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.position}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                      </article>
                    </section>

                    <section className="form-sec-2col">
                      <article className="col-form" style={articleStyle}>
                        <Form.Label>
                          Are you a United States citizen?
                        </Form.Label>
                        <Row className="mb-3">
                          <Form.Group as={Col} sm={5}>
                            <Form.Group
                              className="mb-3"
                              controlId="isCitizenField"
                            >
                              <Form.Select
                                name="isCitizen"
                                value={values.isCitizen}
                                onChange={(e) => {
                                  void setFieldValue(
                                    "isCitizen",
                                    e.target.value
                                  );
                                }}
                                isInvalid={
                                  touched.isCitizen && !!errors.isCitizen
                                }
                                isValid={touched.isCitizen && !errors.isCitizen}
                                onBlur={handleBlur}
                              >
                                <option value="">
                                  Please select your citizenship status
                                </option>
                                <option value="true">
                                  Yes, I am a U.S. citizen
                                </option>
                                <option value="false">
                                  No, I am not a U.S. citizen
                                </option>
                              </Form.Select>
                              <Form.Control.Feedback type="invalid">
                                {errors.isCitizen}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Form.Group>
                        </Row>
                        {values.isCitizen === "false" ? (
                          <React.Fragment>
                            <Row className="mb-3">
                              <Form.Group
                                as={Col}
                                controlId="citizenshipCountryField"
                                sm={5}
                              >
                                <Form.Label>
                                  Please select your citizenship country:
                                </Form.Label>
                                <Form.Select
                                  name="citizenshipCountry"
                                  value={values.citizenshipCountry}
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  isInvalid={
                                    touched.citizenshipCountry &&
                                    !!errors.citizenshipCountry
                                  }
                                  isValid={
                                    touched.citizenshipCountry &&
                                    !errors.citizenshipCountry
                                  }
                                >
                                  <option value="" disabled>
                                    Select your citizenship country
                                  </option>
                                  {countryValues.map((item) => (
                                    <option value={item.value} key={item.key}>
                                      {item.description}
                                    </option>
                                  ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                  {errors.citizenshipCountry}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Row>
                            <Row className="mb-3">
                              <Form.Group
                                as={Col}
                                sm={5}
                                controlId="birthCountry"
                              >
                                <Form.Label>
                                  Please select the country you were born in:
                                </Form.Label>
                                <Form.Select
                                  name="birthCountry"
                                  value={values.birthCountry}
                                  onChange={handleChange}
                                  isInvalid={
                                    touched.birthCountry &&
                                    !!errors.birthCountry
                                  }
                                  isValid={
                                    touched.birthCountry && !errors.birthCountry
                                  }
                                  onBlur={handleBlur}
                                >
                                  <option value="" disabled>
                                    Select the country you were born in
                                  </option>
                                  {countryValues.map((item) => (
                                    <option value={item.value} key={item.key}>
                                      {item.description}
                                    </option>
                                  ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                  {errors.birthCountry}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Row>
                          </React.Fragment>
                        ) : (
                          <div></div>
                        )}
                      </article>
                    </section>

                    <section className="form-sec-2col">
                      <article className="col-form" style={articleStyle}>
                        <Form.Label>
                          Would you like to add a trusted contact now?{" "}
                          <InfoTooltip
                            tooltipBody="Federal Law lets you specify someone whom we or our financial partners can contact if we are unable to
                        reach you but need to help with something on your
                        account. "
                          />
                        </Form.Label>
                        <Row className="mb-3">
                          <Form.Group
                            as={Col}
                            sm={2}
                            // PRESERVED (conversion-only): stray `F` boolean
                            // shorthand attribute in the original markup; kept
                            // verbatim, no runtime effect (Form.Group ignores it).
                            F
                            controlId="trustedContact"
                          >
                            <Form.Select
                              name="trustedContact"
                              // @ts-expect-error preserved: the original binds a
                              // boolean state value to the select's string-typed
                              // `value` prop; React coerces it at runtime.
                              value={values.trustedContact}
                              onChange={(e) => {
                                void setFieldValue(
                                  "trustedContact",
                                  booleanValue(e.target.value)
                                );
                              }}
                              onBlur={handleBlur}
                              isInvalid={
                                touched.trustedContact &&
                                !!errors.trustedContact
                              }
                              isValid={
                                touched.trustedContact && !errors.trustedContact
                              }
                            >
                              <option value="false">No</option>
                              <option value="true">Yes</option>
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                              {errors.trustedContact}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                        {values.trustedContact === true ? (
                          <React.Fragment>
                            <Row className="mb-3">
                              <Form.Group
                                as={Col}
                                controlId="formTrustedContactFirstName"
                                sm={4}
                              >
                                <Form.Label>Contact First Name</Form.Label>
                                <Form.Control
                                  name="trustedFirstName"
                                  placeholder="First"
                                  value={values.trustedFirstName}
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  isInvalid={
                                    touched.trustedFirstName &&
                                    !!errors.trustedFirstName
                                  }
                                  isValid={
                                    touched.trustedFirstName &&
                                    !errors.trustedFirstName
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.trustedFirstName}
                                </Form.Control.Feedback>
                              </Form.Group>

                              <Form.Group
                                as={Col}
                                controlId="formTrustedContactLastName"
                                sm={4}
                              >
                                <Form.Label>Contact Last Name</Form.Label>
                                <Form.Control
                                  name="trustedLastName"
                                  placeholder="Last"
                                  value={values.trustedLastName}
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  isInvalid={
                                    touched.trustedLastName &&
                                    !!errors.trustedLastName
                                  }
                                  isValid={
                                    touched.trustedLastName &&
                                    !errors.trustedLastName
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.trustedLastName}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Row>
                            <Row className="mb-3">
                              <Form.Group
                                as={Col}
                                sm={8}
                                controlId="formTrustedContactEmail"
                              >
                                <Form.Label>Contact Email Address</Form.Label>
                                <Form.Control
                                  type="email"
                                  name="trustedEmail"
                                  placeholder="contact@gmail.com"
                                  onBlur={handleBlur}
                                  value={values.trustedEmail}
                                  onChange={handleChange}
                                  isInvalid={
                                    touched.trustedEmail &&
                                    !!errors.trustedEmail
                                  }
                                  isValid={
                                    touched.trustedEmail && !errors.trustedEmail
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.trustedEmail}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Row>
                          </React.Fragment>
                        ) : (
                          <div></div>
                        )}
                      </article>
                    </section>
                    <section className="form-sec-2col">
                      <article className="col-form">
                        <div className="submit-row">
                          {this.props.error && (
                            <Alert type="error" msg={this.props.error} />
                          )}
                          <Button
                            name="submit"
                            withArrow={true}
                            loading={this.props.isFetching}
                          />
                        </div>
                      </article>
                    </section>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  };
}

export default connector(withApollo<Props>(IndividualExtraDetails));
